import React, { useEffect, useState, useContext } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import { newValidator } from 'utils';
import ImageUpload from '../../../../../editor/internals/imageUpload';
import FlatButton from '../../../../../../app/components/buttons/flat/Flat';
import { addStaffMember, editStaffMember } from '../../api';
import firebase from '../../../../../../firebase';
import './styles.css';
import { countries } from '../../../../../distribute/utils';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  // InputAdornment,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Trans, useTranslation } from 'react-i18next';
import { StaffContext } from '../../modules/staff/context/StaffContext';

const useStyles = makeStyles(() => ({
  textField: {
    '& label.MuiInputLabel-root': {
      color: '#000000',
      fontFamily: 'Raleway, sans-serif',
      fontWeight: 600,
    },
    '& label.Mui-focused': {
      color: '#000000',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#000000',
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: '#000000',
    },
    '& .css-ghsjzk-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled):before': {
      borderBottomColor: '#000000',
    },
  },
  formControl: {
    '& label': {
      fontFamily: 'Raleway, sans-serif',
      fontWeight: 400,
      fontSize: 14,
      color: '#000000',
      left: -15,
    },
    '& .MuiSelect-icon': {
      color: '#000000',
    },
    '& .MuiSelect-standard': {
      color: '#000000',
      fontFamily: 'Raleway, sans-serif',
      fontWeight: 600,
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#000000',
    },
    '& .css-1480iag-MuiInputBase-root-MuiInput-root:after': {
      borderBottomColor: '#000000',
    },
    '& .MuiSelect-select:before': {
      borderBottomColor: '#000000',
    },
    '& .css-1480iag-MuiInputBase-root-MuiInput-root:hover:not(.Mui-disabled):before': {
      borderBottomColor: '#000000',
    },
  },
}));

const StaffMember = ({
  appname,
  open,
  staffMember,
  onAddStaffMemberDialog,
  isImageUploaded,
  onStaffMemberChange,
  onResetStaffData,
  allStaffData,
}) => {
  const { t } = useTranslation();
  const {
    name,
    setName,
    email,
    setEmail,
    mobile,
    setMobile,
    imageUrl,
    setImageUrl,
    isButtonDisabled,
    setIsButtonDisabled,
    dialCode,
    setDialCode,
  } = useContext(StaffContext);
  const isUpdate = Object.keys(staffMember).length > 0;
  const classes = useStyles();

  const { mutateAsync, isLoading } = useMutation(
    isUpdate ? editStaffMember : addStaffMember,
  );
  const queryClient = useQueryClient();
  const accountId = useSelector(state => state.account.organisation.key);

  const getImgUrl = () => `/bookings/${appname}/temp_images/staff/${accountId}`;

  useEffect(() => {
    const getImageUrl = async () => {
      const imageRef = firebase.database().ref(getImgUrl());
      const snapshot = await imageRef.once('value');
      const value = snapshot.val();
      setImageUrl(value);
    };

    isImageUploaded && getImageUrl();
  }, [isImageUploaded]);

  useEffect(() => {
    if (isUpdate) {
      setName(staffMember.name);
      setEmail(staffMember.email);
      setMobile(staffMember.mobile);
      setImageUrl(staffMember.imageUrl);
      setDialCode(staffMember.dialCode);
    }
  }, [staffMember]);

  const handleSubmit = async e => {
    e.preventDefault();

    let payload = {
      appname,
      name,
      email,
      mobile,
      dialCode,
      imageUrl,
      key: isUpdate ? staffMember.key : null,
    };

    setIsButtonDisabled(true);
    await mutateAsync(payload);
    queryClient.invalidateQueries('staffMembers');
    await firebase
      .database()
      .ref(getImgUrl())
      .remove();
    onAddStaffMemberDialog(false);
    setIsButtonDisabled(false);
    onResetStaffData();
  };

  const handleClose = async () => {
    await firebase
      .database()
      .ref(getImgUrl())
      .remove();
    setName('');
    setEmail('');
    setMobile('');
    setImageUrl('');
    setIsButtonDisabled(false);
    onAddStaffMemberDialog(false);
    onResetStaffData();
  };

  const emailIsInvalid = () => {
    if (email && allStaffData) {
      if (
        Object.keys(allStaffData).findIndex(
          staffKey =>
            allStaffData[staffKey].email.toLowerCase() ===
              email.toLowerCase() && staffMember.key !== staffKey,
        ) >= 0
      ) {
        return t('Email already exists');
      }

      const emailValidator = newValidator({
        type: 'email',
        required: true,
      });
      if (!emailValidator(email)) {
        return t('Invalid email');
      }
    }
    return '';
  };

  const mobileIsInvalid = () => {
    if (mobile && allStaffData) {
      if (
        Object.keys(allStaffData).findIndex(
          staffKey =>
            allStaffData[staffKey].mobile.toLowerCase() ===
              mobile.toLowerCase() &&
            allStaffData[staffKey].dialCode &&
            allStaffData[staffKey].dialCode.toLowerCase() === dialCode &&
            staffMember.key !== staffKey,
        ) >= 0
      ) {
        return t('Mobile number already exists');
      }
      const moblieValidator = newValidator({
        type: 'international',
        required: true,
      });
      if (!moblieValidator(mobile)) {
        return t('Invalid mobile number');
      }
    }
    return '';
  };

  const isStaffMemberInvalid =
    isButtonDisabled ||
    !name ||
    !email ||
    !mobile ||
    emailIsInvalid() ||
    mobileIsInvalid();

  const countryOptions = countries().map(country => (
    <MenuItem key={country.dialCode} value={country.dialCode}>
      {country.name} ({country.dialCode})
    </MenuItem>
  ));
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      disableEnforceFocus={true}
    >
      <div className="staff-modal-content">
        <DialogTitle
          style={{
            color: '#261ed5',
            fontFamily: 'Raleway, sans-serif',
            fontWeight: 600,
            fontSize: 24,
            marginTop: 40,
          }}
        >
          {isUpdate ? t('Edit Staff Member') : t('Add a Staff Member')}
        </DialogTitle>
        <div className="field-wrapper">
          <div className="dialog-label">
            <Trans>Image</Trans>
          </div>
          <ImageUpload
            src={imageUrl}
            avatar={true}
            width="180px"
            height="180px"
            targetId="staff_image_upload"
            label={t('Image')}
            caption={t('This image will be displayed for appointments')}
            firebaseUrl={getImgUrl()}
            title={t('Appointment Image')}
            tooltip={t('Minimum image size 240x240 pixels')}
            showCropAreaSizes
            type="image"
            fileType="png"
          />
        </div>

        <div className="field-wrapper">
          <TextField
            className={classes.textField}
            type="text"
            label={t('Name')}
            value={name}
            required
            fullWidth
            variant="standard"
            onChange={e => setName(e.target.value)}
            inputProps={{
              style: { fontFamily: 'Raleway, sans-serif' },
            }}
          />
        </div>

        <div className="field-wrapper">
          <TextField
            className={classes.textField}
            type="email"
            label={t('Email')}
            value={email}
            required
            fullWidth
            variant="standard"
            onChange={e => setEmail(e.target.value)}
            helperText={emailIsInvalid()}
            error={emailIsInvalid()}
            inputProps={{
              style: { fontFamily: 'Raleway, sans-serif' },
            }}
          />
        </div>

        <div className="field-wrapper">
          <FormControl fullWidth className={classes.formControl}>
            <InputLabel id="staff-contact-label">
              <Trans>Select Country Dial Code</Trans>
            </InputLabel>

            <Select
              labelId="staff-contact-label"
              id="staff-member-select"
              variant="standard"
              value={dialCode}
              onChange={e => setDialCode(e.target.value)}
            >
              {countryOptions}
            </Select>
          </FormControl>
        </div>

        <div
          className="field-wrapper"
          style={{
            marginBottom: 60,
          }}
        >
          <TextField
            className={classes.textField}
            type="tel"
            label={t('Mobile')}
            value={mobile}
            InputProps={{
              // startAdornment: <InputAdornment>{dialCode}</InputAdornment>,
              style: { fontFamily: 'Raleway, sans-serif' },
            }}
            required
            fullWidth
            variant="standard"
            onChange={e => setMobile(e.target.value)}
            helperText={mobileIsInvalid()}
            error={mobileIsInvalid()}
          />
        </div>

        <div>
          <FlatButton
            label={isUpdate ? t('Save') : t('ADD')}
            onClick={handleSubmit}
            primary
            disabled={isStaffMemberInvalid}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default StaffMember;
